import React, { useEffect, useState, useCallback, useRef } from 'react';
import { VideoGenerationJob, VideoGenerationSource } from '../../services/api/videoGenerationService';
import videoGenerationService from '../../services/api/videoGenerationService';
import videoJobStreamService, { VideoJobSSEData } from '../../services/sse/videoJobStreamService';
import GeneratingVideoJobCard from './GeneratingVideoJobCard';
import VideoJobDetailModal from './VideoJobDetailModal';
import { resubmitVideoJob, VideoJobResubmitOverrides } from './resubmitVideoJob';
import { Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { usePolling } from '../../hooks/usePolling';

interface GeneratingVideoJobsSectionProps {
  onJobCompleted?: () => void;
  generationSource?: VideoGenerationSource;
  refreshKey?: number;
}

const GeneratingVideoJobsSection: React.FC<GeneratingVideoJobsSectionProps> = ({ onJobCompleted, generationSource, refreshKey = 0 }) => {
  const [jobs, setJobs] = useState<VideoGenerationJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [jobProgress, setJobProgress] = useState<Record<string, { step: string; percentage: number }>>({});
  const jobsRef = useRef<VideoGenerationJob[]>([]);
  const loadedRef = useRef(false);
  const requestSequence = useRef(0);
  const completionCallback = useRef(onJobCompleted);
  completionCallback.current = onJobCompleted;
  const refreshKeyRef = useRef(refreshKey);
  refreshKeyRef.current = refreshKey;

  const loadJobs = useCallback(async () => {
    const sequence = ++requestSequence.current;
    try {
      const fetchedJobs = await videoGenerationService.getJobs(undefined, generationSource);
      if (sequence !== requestSequence.current) return;
      const previous = new Map(jobsRef.current.map((job) => [job.id, job.status]));
      // 高速生成では初回の一覧取得時点ですでに完了している場合がある。
      const newlyCompleted = (loadedRef.current || refreshKeyRef.current > 0) && fetchedJobs.some(
        (job) => job.status === 'completed' && previous.get(job.id) !== 'completed'
      );
      jobsRef.current = fetchedJobs;
      loadedRef.current = true;
      setJobs(fetchedJobs);
      // A disconnected stream must not override newer polling progress.
      setJobProgress((current) => Object.fromEntries(
        Object.entries(current).filter(([id]) =>
          videoJobStreamService.isStreaming(id) && fetchedJobs.some(
            (job) => job.id === id && (job.status === 'pending' || job.status === 'processing')
          )
        )
      ));
      if (newlyCompleted) completionCallback.current?.();
    } finally {
      if (sequence === requestSequence.current) {
        setLoading(false);
      }
    }
  }, [generationSource]);

  const activeJobIds = jobs.filter((job) => ['pending', 'processing'].includes(job.status)).map((job) => job.id).join(',');
  const hasActiveFalJobs = jobs.some((job) => ['pending', 'processing'].includes(job.status) && job.video_model?.startsWith('fal-h3-max'));

  // SSEが届かない場合も、falの短い生成を30秒待たせず表示する。
  // 非表示タブでは止め、403/404 などで止まり、通信断では間隔を延ばす。
  // 定期取得は前回の応答を待ってから次を予約するので重ならない。明示的な再取得と
  // 重なった場合は requestSequence で新しい方の結果だけを反映する。
  const { run: fetchJobs } = usePolling(loadJobs, {
    enabled: activeJobIds !== '',
    intervalMs: hasActiveFalJobs ? 2000 : 30000,
  });

  // SSE接続管理のuseEffect
  useEffect(() => {
    const ids = activeJobIds ? activeJobIds.split(',') : [];
    ids.forEach((id) => {
      if (!videoJobStreamService.isStreaming(id)) {
        videoJobStreamService.startStream(id, {
          onProgress: (data: VideoJobSSEData) => {
            setJobProgress((prev) => ({
              ...prev,
              [data.job_id]: { step: data.progress_step, percentage: data.progress_percentage },
            }));
          },
          onComplete: () => {
            fetchJobs();
          },
        });
      }
    });

    return () => {
      videoJobStreamService.stopAll();
    };
  }, [activeJobIds, fetchJobs]);

  // fetchJobsをjobs依存にすると setJobs → effect → fetch の無限ループになる。
  useEffect(() => {
    jobsRef.current = [];
    loadedRef.current = false;
    return () => { requestSequence.current += 1; };
  }, [generationSource]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs, loadJobs, refreshKey]);

  const handleJobClick = (jobId: string) => {
    setSelectedJobId(jobId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedJobId(null);
  };

  const handleJobCancelled = () => {
    fetchJobs();
  };

  // 失敗ジョブの作り直し。送信は新規生成と同じ generate-async を通り、
  // 一覧はここで一度だけ取り直す（ポーリングは増やさない）。
  const handleResubmit = useCallback(
    async (job: VideoGenerationJob, overrides: VideoJobResubmitOverrides) => {
      await resubmitVideoJob(job, overrides);
      await fetchJobs();
    },
    [fetchJobs]
  );

  // Filter to show only recent jobs (pending, processing, or completed within last hour)
  const displayJobs = jobs.filter((job) => {
    if (job.status === 'pending' || job.status === 'processing') {
      return true;
    }
    // Show completed/failed jobs for 1 hour
    const createdAt = new Date(job.created_at);
    const hourAgo = new Date(Date.now() - 60 * 60 * 1000);
    return createdAt > hourAgo;
  });

  if (loading) {
    return (
      <div role="status" aria-label="動画ジョブを読み込み中" className="flex items-center justify-center py-4">
        <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (displayJobs.length === 0) {
    return null;
  }

  const activeCount = displayJobs.filter(j => j.status === 'pending' || j.status === 'processing').length;
  const completedCount = displayJobs.filter(j => j.status === 'completed').length;
  const failedCount = displayJobs.filter(j => j.status === 'failed').length;

  return (
    <div className="mb-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between py-2 px-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-sm"
      >
        <div className="flex items-center gap-3">
          <span className="font-medium text-gray-700">動画ジョブ</span>
          <div className="flex items-center gap-2 text-xs">
            {activeCount > 0 && (
              <span className="flex items-center gap-1 text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                <Loader2 className="w-3 h-3 animate-spin" />
                進行中 {activeCount}件
              </span>
            )}
            {completedCount > 0 && (
              <span className="text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                完了 {completedCount}件
              </span>
            )}
            {failedCount > 0 && (
              <span className="text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                失敗 {failedCount}件
              </span>
            )}
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        )}
      </button>

      {isExpanded && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
          {displayJobs.map((job) => (
            <GeneratingVideoJobCard
              key={job.id}
              job={job}
              onClick={() => handleJobClick(job.id)}
              progressStep={jobProgress[job.id]?.step}
              progressPercentage={jobProgress[job.id]?.percentage}
            />
          ))}
        </div>
      )}

      {selectedJobId && (
        <VideoJobDetailModal
          jobId={selectedJobId}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onJobCancelled={handleJobCancelled}
          onResubmit={handleResubmit}
        />
      )}
    </div>
  );
};

export default GeneratingVideoJobsSection;
